import { useCallback, useEffect, useMemo, useState } from 'react';
import { getTourAnchors } from '../../../services/admin-tours';
import type { AdminTourAnchor, AdminTourPlatformKey } from '../../../types/adminTours';
import { PLATFORM_VALUES } from '../../../types/adminTours';
import type { AnchorLoadStatus } from './AnchorPicker';

export interface TourAnchorsState {
  anchors: AdminTourAnchor[];
  status: AnchorLoadStatus;
  retry: () => void;
  /** The label for an anchor id, or the id itself when the list has not loaded. */
  labelFor: (anchorId: string) => string;
}

/**
 * Every anchor one platform offers.
 *
 * Loaded with the editor rather than when the picker opens, because two screens need it: the picker
 * itself, and the step the editor creates from `?newStepAnchor=` when web pick mode hands control
 * back. A failure is not fatal - the picker says so and still offers a centered step.
 */
export const useTourAnchors = (platform: AdminTourPlatformKey): TourAnchorsState => {
  const [anchors, setAnchors] = useState<AdminTourAnchor[]>([]);
  const [status, setStatus] = useState<AnchorLoadStatus>('idle');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    getTourAnchors(PLATFORM_VALUES[platform], controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setAnchors(next);
        setStatus('ready');
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        console.warn(`Could not load the ${platform} anchor list:`, error);
        setAnchors([]);
        setStatus('failed');
      });
    return () => controller.abort();
  }, [platform, attempt]);

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  const labelFor = useCallback(
    (anchorId: string) => anchors.find((anchor) => anchor.id === anchorId)?.label ?? anchorId,
    [anchors]
  );

  // Memoised: the editor's `addStep` closes over this object, and the `?newStepAnchor=` effect
  // closes over `addStep`. A fresh object per render would re-run an effect that appends a step,
  // leaving a ref guard as the only thing between the page and a duplicate.
  return useMemo(
    () => ({ anchors, status, retry, labelFor }),
    [anchors, status, retry, labelFor]
  );
};
